<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header (matching create page) -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="2" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        Edit Education Detail
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Modify educational qualification details and
                        verification status
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button -->
                    <v-btn
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                    >
                      <v-icon left>{{
                        isEditMode ? "mdi-eye" : "mdi-pencil"
                      }}</v-icon>
                      {{ isEditMode ? "Read Only" : "Edit Mode" }}
                    </v-btn>

                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Education Details
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">Education Information</h2>
              <v-spacer></v-spacer>

              <!-- Verification Status Chip -->
              <v-chip
                :color="getStatusColor(educationDetail.status)"
                dark
                small
                class="mr-2"
              >
                <v-icon left small>
                  {{ getStatusIcon(educationDetail.status) }}
                </v-icon>
                {{ getStatusText(educationDetail.status) }}
              </v-chip>

              <!-- Edit Mode Indicator -->
              <v-chip
                :color="isEditMode ? 'success' : 'warning'"
                :outlined="!isEditMode"
                x-small
              >
                <v-icon left x-small>
                  {{ isEditMode ? "mdi-pencil" : "mdi-eye" }}
                </v-icon>
                {{ isEditMode ? "Edit Mode" : "Read Only Mode" }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Educational Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-school</v-icon>
                    Educational Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="form.user_id"
                        :items="usersList"
                        item-value="id"
                        item-text="display_name"
                        label="User"
                        placeholder="Select user"
                        outlined
                        :disabled="!isEditMode || !canSelectUser"
                        :readonly="!isEditMode || !canSelectUser"
                        :rules="[(v) => !!v || 'User is required']"
                        :error-messages="errors.user_id"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-select>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.qualification"
                        label="Qualification"
                        placeholder="e.g., MBBS, MD, BAMS, PhD"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Qualification is required']"
                        :error-messages="errors.qualification"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-certificate</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.university"
                        label="University/College"
                        placeholder="Name of university or college"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.university"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-school</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.specialization"
                        label="Specialization"
                        placeholder="e.g., Cardiology, Ayurveda, General Medicine"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.specialization"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-medical-bag</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.year_of_passing"
                        label="Year of Passing"
                        placeholder="e.g., 2020"
                        outlined
                        type="number"
                        :min="1950"
                        :max="currentYear + 5"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="yearRules"
                        :error-messages="errors.year_of_passing"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-switch
                        v-model="form.is_active"
                        label="Active Status"
                        color="primary"
                        :disabled="!isEditMode"
                        hide-details="auto"
                        class="mt-2"
                      ></v-switch>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        :value="yearsOfExperience"
                        label="Years of Experience"
                        outlined
                        readonly
                        disabled
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-clock</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Certificate Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-file-document</v-icon
                    >
                    Certificate Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-file-input
                        v-if="isEditMode"
                        v-model="certificateFile"
                        label="Update Certificate/Degree Document"
                        placeholder="Upload new certificate (PDF, JPG, PNG)"
                        outlined
                        accept="application/pdf,image/jpeg,image/png,image/jpg"
                        :rules="fileRules"
                        :error-messages="errors.certificate_file"
                        class="professional-field"
                        hide-details="auto"
                        prepend-icon=""
                        @change="handleFileUpload"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-file-upload</v-icon>
                        </template>
                      </v-file-input>

                      <v-text-field
                        v-else
                        :value="
                          educationDetail.certificate_file_path
                            ? 'Certificate Available'
                            : 'No Certificate'
                        "
                        label="Certificate Status"
                        outlined
                        readonly
                        disabled
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">
                            {{
                              educationDetail.certificate_file_path
                                ? "mdi-file-document"
                                : "mdi-file-document-outline"
                            }}
                          </v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col
                      cols="12"
                      md="6"
                      v-if="educationDetail.certificate_file_path"
                    >
                      <div class="d-flex align-center">
                        <v-btn
                          color="primary"
                          outlined
                          @click="downloadCertificate"
                          class="mr-2"
                        >
                          <v-icon left>mdi-download</v-icon>
                          Download Certificate
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>

                  <div v-if="isEditMode" class="text-caption grey--text mt-1">
                    Supported formats: PDF, JPG, PNG. Maximum size: 5MB
                  </div>
                </div>

                <!-- Verification Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-check-circle</v-icon
                    >
                    Verification Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        :value="getStatusText(educationDetail.status)"
                        label="Verification Status"
                        outlined
                        readonly
                        disabled
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon
                            :color="getStatusColor(educationDetail.status)"
                          >
                            {{ getStatusIcon(educationDetail.status) }}
                          </v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        :value="
                          educationDetail.verified_by
                            ? educationDetail.verified_by.first_name +
                              ' ' +
                              educationDetail.verified_by.last_name
                            : 'Not verified'
                        "
                        label="Verified By"
                        outlined
                        readonly
                        disabled
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account-check</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        :value="
                          educationDetail.verified_at
                            ? formatDate(educationDetail.verified_at)
                            : 'Not verified'
                        "
                        label="Verified At"
                        outlined
                        readonly
                        disabled
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar-check</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Verification Actions (Admin only) -->
                  <v-row v-if="canVerify">
                    <v-col cols="12">
                      <v-divider class="mb-4"></v-divider>
                      <div class="d-flex align-center">
                        <span class="text-body-2 font-weight-medium mr-4">
                          Admin Actions:
                        </span>
                        <v-btn
                          :color="
                            educationDetail.status ? 'warning' : 'success'
                          "
                          :outlined="educationDetail.status"
                          @click="toggleVerification"
                          :loading="verificationLoading"
                          small
                        >
                          <v-icon left>
                            {{
                              educationDetail.status
                                ? "mdi-close-circle"
                                : "mdi-check-circle"
                            }}
                          </v-icon>
                          {{
                            educationDetail.status
                              ? "Remove Verification"
                              : "Mark as Verified"
                          }}
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Form Actions -->
                <div v-if="isEditMode" class="form-actions mt-4">
                  <v-row>
                    <v-col cols="12">
                      <v-divider class="mb-4"></v-divider>
                      <div class="d-flex justify-end align-center">
                        <v-btn
                          color="grey"
                          outlined
                          @click="resetForm"
                          class="mr-3"
                          :disabled="loading"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Reset Changes
                        </v-btn>
                        <v-btn
                          color="grey darken-1"
                          outlined
                          @click="toggleEditMode"
                          class="mr-3"
                          :disabled="loading"
                        >
                          <v-icon left>mdi-close</v-icon>
                          Cancel Edit
                        </v-btn>
                        <v-btn
                          color="primary"
                          @click="updateEducationDetail"
                          :loading="loading"
                          :disabled="!valid || loading"
                        >
                          <v-icon left>mdi-content-save</v-icon>
                          Update Education Detail
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import { mapGetters } from "vuex";

export default {
  name: "EducationDetailEdit",

  data() {
    return {
      valid: false,
      loading: false,
      verificationLoading: false,
      isEditMode: false,
      certificateFile: null,
      usersList: [],
      currentYear: new Date().getFullYear(),
      educationDetail: {},

      form: {
        user_id: null,
        qualification: "",
        university: "",
        specialization: "",
        year_of_passing: null,
        is_active: true,
      },

      originalForm: {},
      errors: {},

      yearRules: [
        (v) => {
          if (!v) return true; // Optional field
          const year = parseInt(v);
          if (year < 1950) return "Year cannot be before 1950";
          if (year > this.currentYear + 5)
            return `Year cannot be more than ${this.currentYear + 5}`;
          return true;
        },
      ],

      fileRules: [
        (v) => {
          if (!v) return true; // Optional field
          if (v.size > 5 * 1024 * 1024)
            return "File size must be less than 5MB";
          const allowedTypes = [
            "application/pdf",
            "image/jpeg",
            "image/png",
            "image/jpg",
          ];
          if (!allowedTypes.includes(v.type))
            return "Only PDF, JPG, and PNG files are allowed";
          return true;
        },
      ],
    };
  },

  computed: {
    ...mapGetters(["user", "role"]),

    canSelectUser() {
      // Only admins can select different users
      return this.role && ["SUPER ADMIN", "ADMIN"].includes(this.role.name);
    },

    canVerify() {
      return this.role && ["SUPER ADMIN", "ADMIN"].includes(this.role.name);
    },

    yearsOfExperience() {
      if (!this.form.year_of_passing) return "N/A";
      const years = this.currentYear - parseInt(this.form.year_of_passing);
      return years >= 0 ? years + " years" : "Future graduate";
    },
  },

  mounted() {
    this.loadEducationDetail();
    this.loadUsers();
  },

  methods: {
    async loadEducationDetail() {
      try {
        this.loading = true;
        const response = await axios.get(
          `/education_details/${this.$route.params.id}`
        );

        if (response.data.success) {
          this.educationDetail = response.data.data;
          this.populateForm();
        } else {
          this.$store.dispatch(
            "errors/setError",
            response.data.message || "Failed to load education detail"
          );
          this.$router.push("/education_details");
        }
      } catch (error) {
        console.error("Error loading education detail:", error);
        this.$store.dispatch(
          "errors/setError",
          "Error loading education detail"
        );
        this.$router.push("/education_details");
      } finally {
        this.loading = false;
      }
    },

    async loadUsers() {
      try {
        const response = await axios.get("/users", {
          params: { rowsPerPage: -1 },
        });

        if (response.data.success) {
          this.usersList = response.data.data.map((user) => ({
            id: user.id,
            display_name: `${user.first_name} ${user.last_name}`,
          }));
        }
      } catch (error) {
        console.error("Error loading users:", error);
      }
    },

    populateForm() {
      this.form = {
        user_id: this.educationDetail.user_id,
        qualification: this.educationDetail.qualification || "",
        university: this.educationDetail.university || "",
        specialization: this.educationDetail.specialization || "",
        year_of_passing: this.educationDetail.year_of_passing,
        is_active: this.educationDetail.is_active,
      };

      // Store original form for reset functionality
      this.originalForm = { ...this.form };
    },

    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      if (!this.isEditMode) {
        this.resetForm();
      }
    },

    handleFileUpload(file) {
      this.certificateFile = file;
      if (file && this.errors.certificate_file) {
        this.errors.certificate_file = null;
      }
    },

    downloadCertificate() {
      if (this.educationDetail.certificate_file_path) {
        const mediaUrl = this.$store.getters["settings/getMediaUrl"];
        const url = `${mediaUrl}${this.educationDetail.certificate_file_path}`;
        const link = document.createElement("a");
        link.href = url;
        link.download = `certificate_${this.educationDetail.id}`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    async toggleVerification() {
      try {
        this.verificationLoading = true;
        const endpoint = this.educationDetail.status ? "unverify" : "verify";
        const response = await axios.post(
          `/education_details/${this.educationDetail.id}/${endpoint}`
        );

        if (response.data.success) {
          this.$store.dispatch("success/setSuccess", response.data.message);
          this.loadEducationDetail(); // Reload to get updated verification info
        } else {
          this.$store.dispatch("errors/setError", response.data.message);
        }
      } catch (error) {
        console.error("Error updating verification status:", error);
        this.$store.dispatch(
          "errors/setError",
          "Error updating verification status"
        );
      } finally {
        this.verificationLoading = false;
      }
    },

    async updateEducationDetail() {
      // Validate form
      if (!this.$refs.form.validate()) {
        this.$store.dispatch(
          "errors/setError",
          "Please fill in all required fields correctly"
        );
        return;
      }

      try {
        this.loading = true;
        this.errors = {};

        // Create FormData for file upload
        const formData = new FormData();

        // Append form fields
        Object.keys(this.form).forEach((key) => {
          if (this.form[key] !== null && this.form[key] !== "") {
            formData.append(key, this.form[key]);
          }
        });

        // Append certificate file if uploaded
        if (this.certificateFile) {
          formData.append("certificate_file", this.certificateFile);
        }

        const response = await axios.post(
          `/education_details/${this.educationDetail.id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "X-HTTP-Method-Override": "PUT",
            },
          }
        );

        if (response.data.success) {
          this.$store.dispatch(
            "success/setSuccess",
            response.data.message || "Education detail updated successfully"
          );
          this.educationDetail = response.data.data;
          this.populateForm();
          this.isEditMode = false;
          this.certificateFile = null;
        } else {
          this.$store.dispatch(
            "errors/setError",
            response.data.message || "Failed to update education detail"
          );
        }
      } catch (error) {
        console.error("Error updating education detail:", error);

        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          this.errors = error.response.data.errors;
          this.$store.dispatch(
            "errors/setError",
            "Please check the form for errors"
          );
        } else {
          this.$store.dispatch(
            "errors/setError",
            "Error updating education detail"
          );
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = { ...this.originalForm };
      this.certificateFile = null;
      this.errors = {};
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    },

    getStatusColor(status) {
      switch (Number(status)) {
        case 0:
          return "warning"; // Pending
        case 1:
          return "success"; // Verified
        case 2:
          return "error"; // Rejected
        default:
          return "grey";
      }
    },

    getStatusText(status) {
      switch (Number(status)) {
        case 0:
          return "Pending";
        case 1:
          return "Verified";
        case 2:
          return "Rejected";
        default:
          return "Unknown";
      }
    },

    getStatusIcon(status) {
      switch (Number(status)) {
        case 0:
          return "mdi-clock"; // Pending
        case 1:
          return "mdi-check-circle"; // Verified
        case 2:
          return "mdi-close-circle"; // Rejected
        default:
          return "mdi-help-circle";
      }
    },
  },
};
</script>

<style scoped>
.page-header-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 12px;
}

.form-card {
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.form-header {
  background: linear-gradient(135deg, #fafbfc 0%, #e8ebef 100%);
  border-radius: 12px 12px 0 0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0;
}

.page-subtitle {
  font-size: 0.875rem;
  margin-top: 4px;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.form-section {
  background: #fafbfc;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #1976d2;
}

.section-title {
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.professional-field .v-input__control .v-input__slot {
  border-radius: 8px;
}

.professional-field .v-text-field__details {
  padding-left: 12px;
}

.form-actions {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
}

.back-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

/* Disabled field styling */
.professional-field.v-input--is-disabled .v-input__slot {
  background-color: #f5f5f5 !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page-header-card .v-row {
    flex-direction: column;
  }

  .page-header-card .v-col {
    text-align: center;
    margin-bottom: 8px;
  }

  .form-section {
    padding: 16px;
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 1rem;
  }
}
</style>